import { forwardRef } from 'react'
import {
  DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_IMAGE_CLASS,
  DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_SIZE_CLASS,
  DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_SRC,
  DASHBOARD_RESKIN_FINISH_SHIELD_GLOW_CLASS,
  DASHBOARD_RESKIN_FINISH_SHIELD_SIZE_CLASS,
  DASHBOARD_RESKIN_FINISH_SHIELD_SRC,
  DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_SIZE_CLASS,
  DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_SRC,
  DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_TRACK_CLASS,
  DASHBOARD_RESKIN_HEALTHBAR_OUTLINE_SIZE_CLASS,
  DASHBOARD_RESKIN_HEALTHBAR_OUTLINE_SRC,
  DASHBOARD_RESKIN_HEALTHBAR_ROW_CLASS,
  DASHBOARD_RESKIN_HEALTHBAR_TRACK_CLASS,
  DASHBOARD_RESKIN_HEALTHBAR_WRAP_CLASS,
  DASHBOARD_RESKIN_PLUS_XP_CLASS,
  DASHBOARD_RESKIN_POINTS_FILL_CLASS,
  DASHBOARD_RESKIN_POINTS_FILL_SRC,
  DASHBOARD_RESKIN_POINTS_NUMBER_CLASS,
  getDashboardReskinPointsFillStyle,
  getDashboardReskinPointsFillWidthStyle
} from './dashboardReskinTiles'

interface PointBarProps {
  totalExperiencePoints?: number
  isAddingPoint?: boolean
  plusPoint?: number
  isCompleted?: boolean
};

export const POINT_BAR_HORIZONTAL_INSET_CLASS = 'px-[95px]'

const safeFillPercent = 100
export const PLUS_XP_MIN_POINTS = 15

/** Health bar. Displays the user experience points.
 */
const PointBar = forwardRef<HTMLDivElement, PointBarProps>(({
  totalExperiencePoints = 0,
  isAddingPoint = false,
  plusPoint = 0,
  isCompleted = false
}, ref) => {
  const newPoints = totalExperiencePoints
  const fillWidth = Math.min(newPoints, safeFillPercent)

  return (
    <div className={DASHBOARD_RESKIN_HEALTHBAR_WRAP_CLASS}>
      <div
        ref={ref}
        className={DASHBOARD_RESKIN_HEALTHBAR_ROW_CLASS}
        data-testid='point-bar-container'
      >
        <div className={isCompleted
          ? DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_TRACK_CLASS
          : DASHBOARD_RESKIN_HEALTHBAR_TRACK_CLASS}
        >
          {isCompleted
            ? (
              <>
                <div
                  className='sr-only'
                  role='complementary'
                  aria-label='Training progress'
                  aria-live='polite'
                >
                  <span>Health bar progress: </span>
                  <span>{fillWidth}</span>
                  <span> XP</span>
                </div>
                <img
                  src={DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_SRC}
                  className={DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_SIZE_CLASS}
                  alt=''
                  aria-hidden='true'
                  data-testid='dashboard-reskin-healthbar-outline'
                  data-src={DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_SRC}
                />
              </>
              )
            : (
              <>
                {fillWidth > 0
                  ? (
                    <div
                      className='absolute left-0 top-1/2 z-30 h-[94px] overflow-visible duration-500 ease-out -translate-y-1/2'
                      style={getDashboardReskinPointsFillWidthStyle(fillWidth)}
                      data-testid='dashboard-reskin-healthbar-fill'
                    >
                      <div
                        className={DASHBOARD_RESKIN_POINTS_FILL_CLASS}
                        style={getDashboardReskinPointsFillStyle()}
                        data-src={DASHBOARD_RESKIN_POINTS_FILL_SRC}
                        data-testid='dashboard-reskin-points-fill'
                        aria-hidden='true'
                      />
                      <div
                        className={DASHBOARD_RESKIN_POINTS_NUMBER_CLASS}
                        role='complementary'
                        aria-label='Training progress'
                        aria-live='polite'
                      >
                        <span className='sr-only'>Health bar progress: </span>
                        <span>{fillWidth}</span>
                        <span className='sr-only'> XP</span>
                      </div>
                      {newPoints >= PLUS_XP_MIN_POINTS
                        ? (
                          <div
                            className={DASHBOARD_RESKIN_PLUS_XP_CLASS}
                            style={{ opacity: isAddingPoint ? 1 : 0 }}
                            aria-hidden='true'
                            data-testid='dashboard-reskin-plus-xp'
                          >
                            +{plusPoint} XP
                          </div>
                          )
                        : null}
                    </div>
                    )
                  : (
                    <div
                      className='sr-only'
                      role='complementary'
                      aria-label='Training progress'
                      aria-live='polite'
                    >
                      <span>Health bar progress: </span>
                      <span>0</span>
                      <span> XP</span>
                    </div>
                    )}
                <div
                  className={DASHBOARD_RESKIN_HEALTHBAR_OUTLINE_SIZE_CLASS}
                  style={{
                    backgroundImage: `url(${DASHBOARD_RESKIN_HEALTHBAR_OUTLINE_SRC})`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat'
                  }}
                  data-testid='dashboard-reskin-healthbar-outline'
                  data-src={DASHBOARD_RESKIN_HEALTHBAR_OUTLINE_SRC}
                  aria-hidden='true'
                />
              </>
              )}
        </div>
        <div className={isCompleted
          ? DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_SIZE_CLASS
          : DASHBOARD_RESKIN_FINISH_SHIELD_SIZE_CLASS}
        >
          <img
            src={isCompleted
              ? DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_SRC
              : DASHBOARD_RESKIN_FINISH_SHIELD_SRC}
            className={isCompleted
              ? DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_IMAGE_CLASS
              : DASHBOARD_RESKIN_FINISH_SHIELD_GLOW_CLASS}
            alt=''
            aria-hidden='true'
            data-testid='dashboard-reskin-finish-shield'
          />
        </div>
      </div>
    </div>
  )
})

export default PointBar
